import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import AuditMetricsCard from './components/AuditMetricsCard';
import AuditLogFeed from './components/AuditLogFeed';
import AuditFilters from './components/AuditFilters';
import AuditSummaryPanel from './components/AuditSummaryPanel';
import ActivityTimeline from './components/ActivityTimeline';

const UserActivityAuditLog = () => {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: 'last7days',
    user: 'all',
    actionType: 'all',
    riskLevel: 'all'
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [metrics, setMetrics] = useState({
    totalActions: 0,
    uniqueUsers: 0,
    securityEvents: 0,
    dataAccess: 0
  });
  const [summaryData, setSummaryData] = useState({
    mostActiveUsers: [],
    frequentActions: [],
    securityAlerts: []
  });

  useEffect(() => {
    fetchAuditData();
  }, [filters]);

  const fetchAuditData = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual Supabase service call
      const mockLogs = [
        {
          id: 1,
          userId: 'user-001',
          userName: 'John Doe',
          userRole: 'admin',
          action: 'Login',
          actionCategory: 'Authentication',
          description: 'Successful login from dashboard',
          affectedResource: 'User Session',
          ipAddress: '192.168.1.100',
          sessionId: 'sess-abc123',
          timestamp: new Date()?.toISOString(),
          riskLevel: 'low',
          status: 'success'
        },
        {
          id: 2,
          userId: 'user-002',
          userName: 'Jane Smith',
          userRole: 'analyst',
          action: 'Data Export',
          actionCategory: 'Data Access',
          description: 'Exported transaction report for Q1 2026',
          affectedResource: 'Transaction Data',
          ipAddress: '192.168.1.105',
          sessionId: 'sess-def456',
          timestamp: new Date(Date.now() - 3600000)?.toISOString(),
          riskLevel: 'medium',
          status: 'success'
        },
        {
          id: 3,
          userId: 'user-003',
          userName: 'Mike Johnson',
          userRole: 'merchant',
          action: 'Failed Login',
          actionCategory: 'Authentication',
          description: 'Multiple failed login attempts detected',
          affectedResource: 'User Account',
          ipAddress: '203.45.67.89',
          sessionId: 'sess-ghi789',
          timestamp: new Date(Date.now() - 7200000)?.toISOString(),
          riskLevel: 'high',
          status: 'failed'
        },
        {
          id: 4,
          userId: 'user-001',
          userName: 'John Doe',
          userRole: 'admin',
          action: 'Configuration Change',
          actionCategory: 'System Configuration',
          description: 'Updated payment gateway settings',
          affectedResource: 'System Configuration',
          ipAddress: '192.168.1.100',
          sessionId: 'sess-abc123',
          timestamp: new Date(Date.now() - 10800000)?.toISOString(),
          riskLevel: 'medium',
          status: 'success',
          beforeState: { maxTransactionAmount: 10000000 },
          afterState: { maxTransactionAmount: 15000000 }
        },
        {
          id: 5,
          userId: 'user-004',
          userName: 'Sarah Williams',
          userRole: 'analyst',
          action: 'View Dashboard',
          actionCategory: 'Data Access',
          description: 'Accessed merchant analytics dashboard',
          affectedResource: 'Merchant Analytics',
          ipAddress: '192.168.1.110',
          sessionId: 'sess-jkl012',
          timestamp: new Date(Date.now() - 14400000)?.toISOString(),
          riskLevel: 'low',
          status: 'success'
        }
      ];

      setAuditLogs(mockLogs);

      // Calculate metrics
      setMetrics({
        totalActions: mockLogs?.length,
        uniqueUsers: new Set(mockLogs?.map(log => log?.userId))?.size,
        securityEvents: mockLogs?.filter(log => log?.riskLevel === 'high')?.length,
        dataAccess: mockLogs?.filter(log => log?.actionCategory === 'Data Access')?.length
      });

      // Calculate summary data
      const userActivityCount = {};
      const actionTypeCount = {};
      mockLogs?.forEach(log => {
        userActivityCount[log?.userName] = (userActivityCount?.[log?.userName] || 0) + 1;
        actionTypeCount[log?.action] = (actionTypeCount?.[log?.action] || 0) + 1;
      });

      setSummaryData({
        mostActiveUsers: Object.entries(userActivityCount)
          ?.map(([name, count]) => ({ name, count }))
          ?.sort((a, b) => b?.count - a?.count)
          ?.slice(0, 5),
        frequentActions: Object.entries(actionTypeCount)
          ?.map(([action, count]) => ({ action, count }))
          ?.sort((a, b) => b?.count - a?.count)
          ?.slice(0, 5),
        securityAlerts: mockLogs?.filter(log => log?.riskLevel === 'high')
      });
    } catch (error) {
      console.error('Error fetching audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: 'last7days',
      user: 'all',
      actionType: 'all',
      riskLevel: 'all'
    });
  };

  const handleExport = (format) => {
    console.log(`Exporting audit logs in ${format} format`);
    // Implement export functionality
  };

  return (
    <>
      <Helmet>
        <title>User Activity Audit Log - Payment Gateway Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">User Activity Audit Log</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Comprehensive tracking and analysis of user actions for compliance and security monitoring
              </p>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <AuditMetricsCard
              title="Total Actions"
              value={metrics?.totalActions}
              icon="Activity"
              trend="+12.5%"
              trendType="positive"
              loading={loading}
            />
            <AuditMetricsCard
              title="Unique Users"
              value={metrics?.uniqueUsers}
              icon="Users"
              trend="+5.2%"
              trendType="positive"
              loading={loading}
            />
            <AuditMetricsCard
              title="Security Events"
              value={metrics?.securityEvents}
              icon="Shield"
              trend="-8.3%"
              trendType="positive"
              loading={loading}
            />
            <AuditMetricsCard
              title="Data Access"
              value={metrics?.dataAccess}
              icon="Database"
              trend="+3.7%"
              trendType="neutral"
              loading={loading}
            />
          </div>

          {/* Filters */}
          <AuditFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onExport={handleExport}
          />

          {/* Activity Timeline */}
          <ActivityTimeline data={auditLogs} loading={loading} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Audit Log Feed - 3 columns */}
            <div className="lg:col-span-3">
              <AuditLogFeed data={auditLogs} loading={loading} />
            </div>

            {/* Summary Panel - 1 column */}
            <div className="lg:col-span-1">
              <AuditSummaryPanel data={summaryData} loading={loading} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserActivityAuditLog;